import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, BehaviorSubject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { OrderUtilityService } from './../../../services';
import { OrderFilter, SelectFilter } from './../../../models';
import { DrmType, ErrorType, LineStatus, MediaType, OrderSourceSystem, OrderType } from './../../../models/enums';
import { addDays } from 'date-fns';
import { NgClass } from '@angular/common';
import { MatFormField, MatLabel, MatInput, MatSuffix } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { provideNativeDateAdapter } from '@angular/material/core';

@Component({
  selector: 'insight-filters[filtersSubject]',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  providers: [provideNativeDateAdapter()],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    MatFormField,
    MatLabel,
    MatInput,
    MatSuffix,
    MatButtonModule,
    MatIcon,
    MatCheckbox,
    MatSelect,
    MatSelectTrigger,
    MatOption,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule
  ]
})
export class FiltersComponent implements OnInit, OnDestroy {
  @Input() filtersSubject: BehaviorSubject<OrderFilter>;
  @Input() showErrorFilter = true;
  @Input() showDrmFilter = true;
  @Input() showMinShipFilter = true;
  @Input() showMaxShipFilter = true;
  @Input() showOrderFilter = true;
  @Input() showMediaFilter = true;
  @Input() showStatusFilter = true;
  @Input() showOrderSourceSystemFilter = true;
  @Input() showCheckboxFilters = true;
  @Input() shouldSetUrlPrams = true;
  @Input() layoutDirection = 'column';

  public warningsErrors = true;
  public cancelledOrders = false;
  public filterForm: UntypedFormGroup;
  public minToShipDate: Date;
  public errorFilterTypes: SelectFilter[] = [
    new SelectFilter('All Error Types', ErrorType.All),
    new SelectFilter('Other', ErrorType.Unknown),
    new SelectFilter('Threshold Violation', ErrorType.ThresholdViolation),
    new SelectFilter('File Size', ErrorType.FileSizeError),
    new SelectFilter('HD3', ErrorType.Hd3Error),
    new SelectFilter('Schedule', ErrorType.ScheduleError),
    new SelectFilter('Missing Trailer', ErrorType.MissingTrailer),
    new SelectFilter('Missing Content', ErrorType.MissingContent),
    new SelectFilter('Missing Publicity', ErrorType.MissingPublicity)
  ];
  public orderFilterTypes: SelectFilter[] = [
    new SelectFilter('All Order Types', OrderType.All),
    new SelectFilter('ADEBS', OrderType.Adebs),
    new SelectFilter('Cinelink', OrderType.Cinelink),
    new SelectFilter('Streaming Server', OrderType.StreamingServer)
  ];
  public mediaFilterTypes: SelectFilter[] = [
    new SelectFilter('All Media Types', MediaType.All),
    new SelectFilter('CVD', MediaType.Cvd),
    new SelectFilter('Hard Drive', MediaType.HardDrive),
    new SelectFilter('Legacy E-Delivery', MediaType.Electronic),
    new SelectFilter('Network Delivery', MediaType.NetworkDelivery)
  ];
  public statusFilterTypes: SelectFilter[] = [
    new SelectFilter('All Statuses', LineStatus.All),
    new SelectFilter('Pending', LineStatus.Pending),
    new SelectFilter('Watermarking', LineStatus.Watermarking),
    new SelectFilter('Transcoding', LineStatus.Transcoding),
    new SelectFilter('Encrypting', LineStatus.Encrypting),
    new SelectFilter('Packaging', LineStatus.Packaging),
    new SelectFilter('Authoring', LineStatus.Authoring),
    new SelectFilter('PinPlay', LineStatus.PinPlay),
    new SelectFilter('Licensing', LineStatus.Licensing),
    new SelectFilter('Uploading', LineStatus.Uploading),
    new SelectFilter('Burning', LineStatus.Burning),
    new SelectFilter('Pending Download', LineStatus.PendingDownload),
    new SelectFilter('Pending Ship', LineStatus.PendingShipped)
  ];

  public orderSourceSystemFilterTypes: SelectFilter[] = [
    new SelectFilter('All Systems', OrderSourceSystem.All),
    new SelectFilter('Dynamics', OrderSourceSystem.Dynamics),
    new SelectFilter('Arc/Contracts', OrderSourceSystem.ArcContracts)
  ];

  public drmFilterTypes: SelectFilter[] = [
    new SelectFilter('All', DrmType.All),
    new SelectFilter('AES', DrmType.AES),
    new SelectFilter('Cenc', DrmType.Cenc),
    new SelectFilter('Cenc2', DrmType.Cenc2),
    new SelectFilter('Cmaf', DrmType.Cmaf),
    new SelectFilter('Playready', DrmType.Playready),
    new SelectFilter('Proidiom', DrmType.Proidiom),
    new SelectFilter('Samsung LYNK', DrmType.SamsungLynk),
    new SelectFilter('Secret Agent', DrmType.SecretAgent),
    new SelectFilter('Widevine', DrmType.Widevine),
  ];

  // The stored procedures only go back 30 days for the ship date.
  // ADEBS orders can sometimes go out 21 days for the future ship date
  public readonly earliestShipDateShown = new Date(new Date().setDate(new Date().getDate() - 30));
  public readonly latestShipDateShown = new Date(new Date().setDate(new Date().getDate() + 21));

  // These two fields store the current value for the filters that have multiple selection
  private errorFiltersData: number[] = [];
  private orderTypeFilterData: number[] = [];
  private mediaFilterData: number[] = [];

  private unsubscribeTrigger$ = new Subject();

  constructor(public orderUtilityService: OrderUtilityService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router) {

  }

  ngOnInit() {
    this.filterForm = this.formBuilder.group({
      errorFilter: [[ErrorType.All]],
      fromShipDateFilter: '',
      toShipDateFilter: '',
      orderTypeFilter: [[OrderType.All]],
      mediaFilter: [[MediaType.All]],
      statusFilter: LineStatus.All,
      drmFilter: DrmType.All,
      orderSourceSystemFilter: OrderSourceSystem.All,
      warningsErrorsFilter: this.warningsErrors,
      showCancelledOrdersFilter: this.cancelledOrders
    });

    this.route.queryParamMap.pipe(
      take(1),
      tap(q => {
        console.log('Grabbing query parameters');

        // grab original query parameter values
        const statusFilterParams = q.get('status') ? Number(q.get('status')) : [LineStatus.All];
        const drmTypeFilterParam = q.get('drmType') ? Number(q.get('drmType')) : [DrmType.All];
        const orderSourceSystemFilterParam = q.get('orderSourceSystem') ? Number(q.get('orderSourceSystem')) : OrderSourceSystem.All;
        const errorsOnlyParam = q.get('errorsOnly');
        const warningsErrorsFilterParams = errorsOnlyParam !== null ? errorsOnlyParam === 'true' : this.warningsErrors;
        const cancelledOrdersParam = q.get('cancelledOrders');
        const cancelledOrdersFilterParams = cancelledOrdersParam !== null ? cancelledOrdersParam : this.cancelledOrders;
        const toShipDateFilterParams = q.get('maxShip');
        const fromShipDateFilterParams = q.get('minShip');

        const errorFilterParams: string[] = q.getAll('errorType'); // can have multiple string params
        const orderFilterParams: string[] = q.getAll('orderType'); // can have multiple string params
        const mediaFilterParams: string[] = q.getAll('mediaType');

        // update any values with smarter defaults if nothing provided
        let errorFilterValue: number[][] = [];
        if (errorFilterParams.length > 0) {
          errorFilterValue = [this.convertQueryParams(q.getAll('errorType'))];
        } else {
          errorFilterValue = [[ErrorType.All]];
        }

        let fromShipDateValue = null;
        if (fromShipDateFilterParams) {
          fromShipDateValue = new Date();
          fromShipDateValue = addDays(fromShipDateValue, parseInt(q.get('minShip'), 10));
        } else if (this.showMinShipFilter === false) {
          // If we're hiding the date value, then default to the earliest
          fromShipDateValue = null;
        } else {
          const today = new Date();
          fromShipDateValue = addDays(today, -7); // go back 7 days if nothing set
        }

        let toShipDateValue = null;
        if (toShipDateFilterParams) {
          toShipDateValue = new Date();
          toShipDateValue = addDays(toShipDateValue, parseInt(q.get('maxShip'), 10));
        } else if (this.showMaxShipFilter === false) {
          // If we're hiding the date value, then default to the latest
          toShipDateValue = null;
        } else {
          const today = new Date();
          toShipDateValue = addDays(today, 7); // go back 7 days if nothing set
        }

        let orderTypeFilerValue: number[][] = [];
        if (orderFilterParams.length > 0) {
          orderTypeFilerValue = [this.convertQueryParams(q.getAll('orderType'))];
        } else {
          orderTypeFilerValue = [[OrderType.All]];
        }

        let mediaTypeFilterValue: number[][] = [];
        if (mediaFilterParams.length > 0) {
          mediaTypeFilterValue = [this.convertQueryParams(q.getAll('mediaType'))];
        } else {
          mediaTypeFilterValue = [[MediaType.All]];
        }

        // for now we are using the UI form as the model data
        // now that we have added any default logic, set final values
        this.filterForm = this.formBuilder.group({
          errorFilter: errorFilterValue,
          fromShipDateFilter: fromShipDateValue,
          toShipDateFilter: toShipDateValue,
          orderTypeFilter: orderTypeFilerValue,
          mediaFilter: mediaTypeFilterValue,
          statusFilter: statusFilterParams,
          warningsErrorsFilter: warningsErrorsFilterParams,
          cancelledOrdersFilter: cancelledOrdersFilterParams,
          drmFilter: drmTypeFilterParam,
          orderSourceSystemFilter: orderSourceSystemFilterParam
        });

        this.filtersSubject.next(this.filterForm.value);

        // store internal reference for filter types that have multiple selections
        this.errorFiltersData = this.filterForm.controls.errorFilter.value;
        this.orderTypeFilterData = this.filterForm.controls.orderTypeFilter.value;
        this.mediaFilterData = this.filterForm.controls.mediaFilter.value;
      })
    )
      .pipe(takeUntil(this.unsubscribeTrigger$))
      .subscribe();

    this.filterForm.valueChanges
      .pipe(takeUntil(this.unsubscribeTrigger$))
      .subscribe((formFilter: OrderFilter) => {
        /*
        Since the error filter can have multiple errors selected for filtering
        we need to have a check for when the all error types is selected. This
        method is to handle the different states that the error filter can be in.
        */
        if (!this.errorFiltersData.includes(ErrorType.All) && formFilter.errorFilter.includes(ErrorType.All)) {
          this.errorFiltersData = formFilter.errorFilter.filter(e => e === ErrorType.All);
        } else if (this.errorFiltersData.includes(ErrorType.All) && formFilter.errorFilter.length > 1) {
          this.errorFiltersData = formFilter.errorFilter.filter(e => e !== ErrorType.All);
        } else if (formFilter.errorFilter.length === 0) {
          this.errorFiltersData = [ErrorType.All];
        } else {
          this.errorFiltersData = formFilter.errorFilter;
        }
        formFilter.errorFilter = this.errorFiltersData;

        if (formFilter.errorFilter !== this.filterForm.controls.errorFilter.value) {
          this.filterForm.controls.errorFilter.setValue(formFilter.errorFilter);
        }

        /*
        Order type filter can have multiple selections. If "All is selected",
        we need to handle updating everything. Logic is very similar to errors above
        */
        if (!this.orderTypeFilterData.includes(OrderType.All) && formFilter.orderTypeFilter.includes(OrderType.All)) {
          this.orderTypeFilterData = formFilter.orderTypeFilter.filter(e => e === OrderType.All);
        } else if (this.orderTypeFilterData.includes(OrderType.All) && formFilter.orderTypeFilter.length > 1) {
          this.orderTypeFilterData = formFilter.orderTypeFilter.filter(e => e !== OrderType.All);
        } else if (formFilter.orderTypeFilter.length === 0) {
          this.orderTypeFilterData = [OrderType.All];
        } else {
          this.orderTypeFilterData = formFilter.orderTypeFilter;
        }
        formFilter.orderTypeFilter = this.orderTypeFilterData;

        if (formFilter.orderTypeFilter !== this.filterForm.controls.orderTypeFilter.value) {
          this.filterForm.controls.orderTypeFilter.setValue(formFilter.orderTypeFilter);
        }

        /* Same logic...except for media type */
        if (!this.mediaFilterData.includes(MediaType.All) && formFilter.mediaFilter.includes(MediaType.All)) {
          this.mediaFilterData = formFilter.mediaFilter.filter(e => e === MediaType.All);
        } else if (this.mediaFilterData.includes(MediaType.All) && formFilter.mediaFilter.length > 1) {
          this.mediaFilterData = formFilter.mediaFilter.filter(e => e !== MediaType.All);
        } else if (formFilter.mediaFilter.length === 0) {
          this.mediaFilterData = [MediaType.All];
        } else {
          this.mediaFilterData = formFilter.mediaFilter;
        }
        formFilter.mediaFilter = this.mediaFilterData;

        if (formFilter.mediaFilter !== this.filterForm.controls.mediaFilter.value) {
          this.filterForm.controls.mediaFilter.setValue(formFilter.mediaFilter);
        }

        // the filters config object (filtersSubject) has additional values outside this component...
        // just update filter values this deals with and tell it there is an update
        this.filtersSubject.value.orderTypeFilter = formFilter.orderTypeFilter;
        this.filtersSubject.value.errorFilter = formFilter.errorFilter;
        this.filtersSubject.value.fromShipDateFilter = (this.showMinShipFilter) ? formFilter.fromShipDateFilter : null;
        this.filtersSubject.value.toShipDateFilter = (this.showMaxShipFilter) ? formFilter.toShipDateFilter : null;
        this.filtersSubject.value.mediaFilter = formFilter.mediaFilter;
        this.filtersSubject.value.statusFilter = formFilter.statusFilter;
        this.filtersSubject.value.warningsErrorsFilter = formFilter.warningsErrorsFilter;
        this.filtersSubject.value.cancelledOrdersFilter = formFilter.cancelledOrdersFilter;
        this.filtersSubject.value.drmFilter = formFilter.drmFilter;
        this.filtersSubject.value.orderSourceSystemFilter = formFilter.orderSourceSystemFilter;
        this.filtersSubject.next(this.filtersSubject.value);

        if (this.shouldSetUrlPrams) {
          // from and to ship dates are stored as an integer relative to today
          const minShipParam = this.orderUtilityService.dateCompareToToday(formFilter.fromShipDateFilter.toString());
          const maxShipParam = this.orderUtilityService.dateCompareToToday(formFilter.toShipDateFilter.toString());

          const queryParams = {
            errorType: formFilter.errorFilter,
            minShip: minShipParam,
            maxShip: maxShipParam,
            orderType: formFilter.orderTypeFilter,
            mediaType: formFilter.mediaFilter,
            status: formFilter.statusFilter === LineStatus.All ? null : formFilter.statusFilter,
            orderSourceSystem: formFilter.orderSourceSystemFilter === OrderSourceSystem.All ? null : formFilter.orderSourceSystemFilter,
            errorsOnly: formFilter.warningsErrorsFilter ? 'true' : 'false',
            cancelledOrders: formFilter.cancelledOrdersFilter ? 'true' : 'false'
          };

          this.router.navigate([], { queryParams });
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribeTrigger$.next();
    this.unsubscribeTrigger$.complete();
  }

  public changeShipDatesToToday(): void {
    // the idea of this is to see what orders need to go out today
    // this could also include things that needed to go out yesterday...or in the past
    this.filterForm.patchValue({
      fromShipDateFilter: new Date(this.earliestShipDateShown),
      toShipDateFilter: new Date()
    });
  }

  public changeShipDatesToAny(): void {
    // change date range to the max allowed range for orders to show
    this.filterForm.patchValue({
      fromShipDateFilter: new Date(this.earliestShipDateShown),
      toShipDateFilter: new Date(this.latestShipDateShown)
    });
  }

  public calculateToDateMinDateShown(): Date {
    return this.earliestShipDateShown;
  }

  public calculateFromDateMaxDateShown(): Date {
    return this.latestShipDateShown;
  }

  public getErrorAbbreviation(errorCode: string | number): string {
    return this.orderUtilityService.getErrorAbbreviation(`${errorCode}`);
  }

  // Since the certain filter values need to be of type number
  // we need to convert the string values in the url query
  // parameters to numbers.
  private convertQueryParams(params: string[]): number[] {
    const convertedParams = [];

    params.forEach(p => {
      const convertedParam = +p;

      if (!isNaN(convertedParam)) {
        convertedParams.push(convertedParam);
      }
    });

    return convertedParams;
  }
}
